import { formatCurrency } from "@brazilian-utils/brazilian-utils";
import { Box, Flex } from "@chakra-ui/layout";
import { Text, Alert, AlertIcon } from "@chakra-ui/react";
import { Col, Empty, Row } from "antd";
import useFecharRateio from "core/features/rateio/hooks/useFecharRateio";
import useGetContasRateio from "core/features/rateio/hooks/useGetContasRateio";
import { RateioItem } from "core/features/rateio/typings";
import colors from "core/resources/theme/colors";
import { showConfirm } from "infra/helpers/alerta";
import { useState } from "react";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import ContaTable from "ui/components/ContaTable";
import Field from "ui/components/Input/Field";
import InputMoney from "ui/components/Input/InputMoney";
import SelectRateio from "ui/components/Input/SelectRateio";
import { FormArrecadacaoChildrenFormProps } from "./FormArrecadacao";
import { TitleWrapper } from "./TitleWrapper";
import { getDespesasText } from "./helpers";

interface FormRateioProps {
  month: string;
  year: string;
}

function FormRateio({
  control,
  current,
  month,
  year,
}: FormArrecadacaoChildrenFormProps & FormRateioProps) {
  const [selectedRateio, setSelectedRateio] = useState<
    RateioItem | undefined
  >();
  const {
    data: contasRateio,
    isLoading: isLoadingContasRateio,
    isFetching: isFetchingContasRateio,
    refetch: refetchContas,
  } = useGetContasRateio({ id: selectedRateio?.id, year, month });

  const { mutate: fecharRateio, isLoading: isLoadingFecharRateio } =
    useFecharRateio({
      id: selectedRateio?.id,
      year,
      month,
      onSuccess: () => {
        refetchContas();
      },
    });

  const meta = contasRateio?.reduce(
    (acc, next) => {
      acc = {
        total: acc.total + next.valor,
        despesas: acc.despesas + 1,
        fornecedores: acc.fornecedores.add(next.pessoa),
      };
      return acc;
    },
    {
      total: 0,
      despesas: 0,
      fornecedores: new Set<string>(),
    }
  );

  const _isLoadingData = isLoadingContasRateio || isFetchingContasRateio;

  return (
    <Flex
      display={current ? undefined : "none"}
      flexDir="column"
      flex={1}
      w="100%"
    >
      <Box as={Row} mb={8} gutter={16}>
        <Col sm={14}>
          <TitleWrapper>Rateio de despesas</TitleWrapper>
          <Field
            shouldUnregister
            control={control}
            name="rateioId"
            rules={{
              required: current,
            }}
            render={({ field, isInvalid }) => (
              <SelectRateio
                value={field.value}
                onChange={field.onChange}
                isInvalid={isInvalid}
                year={year}
                month={month}
                onSelectRateio={setSelectedRateio}
              />
            )}
          />
        </Col>

        <Col sm={10}>
          <TitleWrapper>Arrecadar(%)</TitleWrapper>
          <Field
            control={control}
            name="percentualRateio"
            rules={{
              required: current,
            }}
            render={({ field, isInvalid }) => (
              <InputMoney
                isInvalid={isInvalid}
                value={field.value}
                onChange={field.onChange}
                maxValue={100}
              />
            )}
          />
        </Col>
      </Box>

      {selectedRateio ? (
        <Flex flexDir="column">
          <Box as={Row} mb={4} gutter={16} alignItems="center">
            <Box as={Col} sm={19}>
              <Alert status={selectedRateio.aberto ? "info" : "warning"}>
                <AlertIcon />
                {selectedRateio.aberto
                  ? "Este rateio ainda está em aberto para lançamento de despesas"
                  : "Este rateio está fechado para lançamento de despesas"}
              </Alert>
            </Box>
            {selectedRateio.aberto ? (
              <Box as={Col} sm={5}>
                <PrimaryButton
                  w="100%"
                  title={
                    !contasRateio?.length ? "Rateio sem contas" : undefined
                  }
                  onClick={() =>
                    showConfirm(
                      "Deseja fechar esse rateio? Não será mais possível adicionar ou remover contas"
                    ).then(({ isConfirmed }) => {
                      isConfirmed && fecharRateio(selectedRateio?.id);
                    })
                  }
                  disabled={
                    isLoadingFecharRateio ||
                    _isLoadingData ||
                    !contasRateio?.length
                  }
                  isLoading={isLoadingFecharRateio || _isLoadingData}
                >
                  Fechar rateio
                </PrimaryButton>
              </Box>
            ) : null}
          </Box>

          {selectedRateio && contasRateio?.length ? (
            <>
              <Box h="310px" overflow="auto" p={2}>
                <ContaTable
                  data={
                    contasRateio
                      ? {
                          items: contasRateio,
                          meta: {
                            currentPage: 1,
                            itemCount: contasRateio.length,
                            itemsPerPage: contasRateio.length,
                            totalItems: contasRateio.length,
                            totalPages: 1,
                          },
                        }
                      : undefined
                  }
                  searchValue={""}
                  onSearch={() => null}
                  enabledSearch={false}
                  isLoading={_isLoadingData}
                />
              </Box>

              <Box as={Row} mb={8} gutter={16} alignItems="center">
                <Box as={Col} sm={19}>
                  <Text color={colors.black700} fontSize={15}>
                    {getDespesasText(meta)}
                  </Text>
                </Box>
                <Box as={Col} sm={5}>
                  <Text
                    color={colors.black}
                    fontSize={17}
                    fontWeight="bold"
                    textAlign="end"
                  >
                    R$ {formatCurrency(meta?.total || 0)}
                  </Text>
                </Box>
              </Box>
            </>
          ) : selectedRateio?.aberto ? (
            <Empty description="Esse rateio ainda não tem contas cadastradas." />
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  );
}

export default FormRateio;
